import React, { useEffect, useRef, useState } from 'react';
import { MindARThree } from 'mind-ar/dist/mindar-image-three.prod.js';
import * as THREE from 'three';
import { supabase } from './utils/supabase.ts';
import { useParams } from "react-router-dom";

export default () => {
  const { key } = useParams();
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [targetURL, setTargetURL] = useState(null);
  const [videoURL, setVideoURL] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Fetch data from Supabase
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from("ar")
          .select("*")
          .eq("id", key)
          .single();

        if (error) {
          console.error("Supabase error:", error);
          setError("Failed to load AR content");
          setIsLoading(false);
          return;
        }

        if (data) {
          setTargetURL(data.target);
          setVideoURL(data.video);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to load AR content");
        setIsLoading(false);
      }
    };

    if (key) {
      fetchData();
    }
  }, [key]);

  // Hide instructions after 3 seconds
  useEffect(() => {
    if (isReady) {
      const timer = setTimeout(() => {
        setShowInstructions(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isReady]);

  // Preload video completely
  useEffect(() => {
    if (!videoURL) return;

    const preloadVideo = document.createElement('video');
    preloadVideo.src = videoURL;
    preloadVideo.preload = 'auto';
    preloadVideo.crossOrigin = "anonymous";

    // Track loading progress
    preloadVideo.addEventListener('progress', () => {
      if (preloadVideo.buffered.length > 0) {
        const bufferedEnd = preloadVideo.buffered.end(preloadVideo.buffered.length - 1);
        const duration = preloadVideo.duration;
        if (duration > 0) {
          const progress = (bufferedEnd / duration) * 100;
          setLoadingProgress(Math.round(progress));
        }
      }
    });

    // When video can play through without buffering
    preloadVideo.addEventListener('canplaythrough', () => {
      setVideoLoaded(true);
      setLoadingProgress(100);
    });

    preloadVideo.addEventListener('error', (e) => {
      console.error('Video preload error:', e);
      setError('Failed to load video');
    });

    preloadVideo.load();

    return () => {
      preloadVideo.src = '';
    };
  }, [videoURL]);

  // Initialize MindAR when data is ready AND video is loaded
  useEffect(() => {
    if (!targetURL || !videoURL || !videoLoaded) return;

    const mindarThree = new MindARThree({
      container: containerRef.current,
      imageTargetSrc: targetURL,
      uiLoading: "no",
      uiScanning: "no",
      uiError: "no"
    });

    const { renderer, scene, camera } = mindarThree;
    const anchor = mindarThree.addAnchor(0);

    // Create video element
    const video = document.createElement('video');
    video.src = videoURL;
    video.loop = true;
    video.muted = isMuted;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('webkit-playsinline', 'webkit-playsinline');
    video.crossOrigin = "anonymous";
    
    // Force video to load completely
    video.load();
    
    // Store video ref for mute control
    videoRef.current = video;

    // Create video texture
    const videoTexture = new THREE.VideoTexture(video);
    videoTexture.minFilter = THREE.LinearFilter;
    videoTexture.magFilter = THREE.LinearFilter;

    // Create plane with video's aspect ratio
    let plane;
    const material = new THREE.MeshBasicMaterial({
      map: videoTexture,
      transparent: true,
      opacity: 1
    });

    // Wait for video metadata to load to get correct dimensions
    video.addEventListener('loadedmetadata', () => {
      const aspectRatio = video.videoWidth / video.videoHeight;
      const width = 1;
      const height = width / aspectRatio;
      
      if (plane) {
        anchor.group.remove(plane);
      }
      
      const geometry = new THREE.PlaneGeometry(width, height);
      plane = new THREE.Mesh(geometry, material);
      anchor.group.add(plane);
    });

    // Create initial plane with default ratio (will be replaced when metadata loads)
    const geometry = new THREE.PlaneGeometry(1, 1);
    plane = new THREE.Mesh(geometry, material);
    anchor.group.add(plane);

    // Play video when target is found
    anchor.onTargetFound = () => {
      video.play().catch(err => console.log("Video play error:", err));
    };

    // Pause video when target is lost
    anchor.onTargetLost = () => {
      video.pause();
    };

    mindarThree.start().then(() => {
      setIsReady(true);
    }).catch(err => {
      console.error("MindAR start error:", err);
      setError("Failed to start AR");
    });

    renderer.setAnimationLoop(() => {
      renderer.render(scene, camera);
    });

    return () => {
      renderer.setAnimationLoop(null);
      mindarThree.stop();
      video.pause();
      video.src = '';
      videoRef.current = null;
    };
  }, [targetURL, videoURL, videoLoaded]);

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    if (videoRef.current) {
      videoRef.current.muted = newMutedState;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
      background: '#000'
    }}>
      {/* AR Container */}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1
        }}
      />

      {/* Loading Overlay */}
      {(isLoading || !videoLoaded) && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          flexDirection: 'column'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(0, 255, 255, 0.2)',
            borderTop: '4px solid #00ffff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }} />
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
          <div style={{
            color: '#fff',
            fontSize: '18px',
            marginBottom: '10px'
          }}>
            {isLoading ? 'Loading AR Content...' : 'Loading Video...'}
          </div>
          <div style={{
            color: '#999',
            fontSize: '14px',
            marginBottom: '15px'
          }}>
            {!isLoading && videoLoaded === false ? `${loadingProgress}%` : 'Please wait'}
          </div>
          {!isLoading && loadingProgress > 0 && (
            <div style={{
              width: '200px',
              height: '4px',
              background: 'rgba(0, 255, 255, 0.2)',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${loadingProgress}%`,
                height: '100%',
                background: '#00ffff',
                transition: 'width 0.3s ease'
              }} />
            </div>
          )}
        </div>
      )}

      {/* Error Overlay */}
      {error && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          padding: '20px'
        }}>
          <div style={{
            background: '#dc2626',
            color: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '500px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '15px'
            }}>
              ⚠️ Error
            </div>
            <div style={{
              fontSize: '16px',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              {error}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#991b1b',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                fontSize: '16px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Top Header */}
      {!isLoading && !error && videoLoaded && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
          padding: '20px',
          zIndex: 10
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #00ffff, #0080ff)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                📱
              </div>
              <div>
                <h1 style={{
                  color: '#fff',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  margin: 0,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  AR Image Tracker
                </h1>
                <p style={{
                  color: '#00ffff',
                  fontSize: '12px',
                  margin: 0
                }}>
                  {isReady ? '● Live' : '○ Initializing...'}
                </p>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center'
            }}>
              {/* Mute/Unmute Button */}
              <button
                onClick={toggleMute}
                style={{
                  background: 'rgba(0, 255, 255, 0.1)',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '18px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {isMuted ? '🔇' : '🔊'}
              </button>

              <div style={{
                background: 'rgba(0, 255, 255, 0.1)',
                border: '1px solid rgba(0, 255, 255, 0.3)',
                borderRadius: '20px',
                padding: '8px 16px',
                color: '#00ffff',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                MindAR
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instructions Card */}
      {isReady && showInstructions && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 255, 255, 0.2)',
          borderRadius: '16px',
          padding: '20px 24px',
          maxWidth: '500px',
          width: 'calc(100% - 40px)',
          zIndex: 10,
          boxShadow: '0 8px 32px rgba(0, 255, 255, 0.15)',
          animation: 'fadeIn 0.5s ease-in'
        }}>
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateX(-50%) translateY(20px); }
                to { opacity: 1; transform: translateX(-50%) translateY(0); }
              }
            `}
          </style>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px'
          }}>
            <div style={{
              fontSize: '32px',
              flexShrink: 0
            }}>
              🎯
            </div>
            <div>
              <h3 style={{
                color: '#fff',
                fontSize: '18px',
                fontWeight: '600',
                margin: '0 0 8px 0',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                Point at Target Image
              </h3>
              <p style={{
                color: '#aaa',
                fontSize: '14px',
                margin: '0',
                lineHeight: '1.5'
              }}>
                Align your camera with the target image to see AR content appear in real-time
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Corner Overlay Frame */}
      {!isLoading && !error && videoLoaded && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: '400px',
          height: '60%',
          maxHeight: '300px',
          pointerEvents: 'none',
          zIndex: 5
        }}>
          {/* Top Left */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '40px',
            height: '40px',
            borderTop: '3px solid #00ffff',
            borderLeft: '3px solid #00ffff',
            borderRadius: '8px 0 0 0'
          }} />

          {/* Top Right */}
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40px',
            height: '40px',
            borderTop: '3px solid #00ffff',
            borderRight: '3px solid #00ffff',
            borderRadius: '0 8px 0 0'
          }} />

          {/* Bottom Left */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '40px',
            height: '40px',
            borderBottom: '3px solid #00ffff',
            borderLeft: '3px solid #00ffff',
            borderRadius: '0 0 0 8px'
          }} />

          {/* Bottom Right */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '40px',
            height: '40px',
            borderBottom: '3px solid #00ffff',
            borderRight: '3px solid #00ffff',
            borderRadius: '0 0 8px 0'
          }} />
        </div>
      )}

      {/* Scanning Animation */}
      {isReady && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: '400px',
          height: '60%',
          maxHeight: '300px',
          pointerEvents: 'none',
          zIndex: 5
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #00ffff, transparent)',
            animation: 'scan 2s ease-in-out infinite',
            opacity: 0.5
          }} />
          <style>
            {`
              @keyframes scan {
                0%, 100% { transform: translateY(0); opacity: 0; }
                50% { transform: translateY(300px); opacity: 0.5; }
              }
            `}
          </style>
        </div>
      )}
    </div>
  );
}